import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Toast notification type
export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
  isVisible: boolean
  timestamp: number
}

// Modal state type
export interface Modal {
  id: string
  type: string
  isOpen: boolean
  data?: unknown
  props?: Record<string, unknown>
}

// Loading state for different features
export interface LoadingState {
  [key: string]: boolean
}

// UI state interface
export interface UIState {
  // Theme and appearance
  theme: 'light' | 'dark' | 'system'
  sidebarOpen: boolean
  sidebarCollapsed: boolean

  // Layout and navigation
  currentPage: string
  breadcrumbs: Array<{ label: string; path?: string }>

  // Notifications and feedback
  toasts: Toast[]
  modals: Modal[]

  // Loading states for various operations
  loading: LoadingState

  // Error states
  errors: Record<string, string>

  // Feature flags (for gradual rollouts)
  features: Record<string, boolean>

  // User preferences
  preferences: {
    compactMode: boolean
    showAdvancedFilters: boolean
    defaultPageSize: number
    autoRefresh: boolean
    refreshInterval: number
  }
}

// Initial state
const initialState: UIState = {
  theme: 'light',
  sidebarOpen: true,
  sidebarCollapsed: false,
  currentPage: '',
  breadcrumbs: [],
  toasts: [],
  modals: [],
  loading: {},
  errors: {},
  features: {
    darkMode: false,
    advancedAnalytics: true,
    exportFeatures: true,
    realTimeUpdates: false,
  },
  preferences: {
    compactMode: false,
    showAdvancedFilters: false,
    defaultPageSize: 25,
    autoRefresh: false,
    refreshInterval: 30000, // 30 seconds
  },
}

// UI slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Theme management
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload
    },

    // Sidebar management
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },

    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload
    },

    toggleSidebar: state => {
      state.sidebarOpen = !state.sidebarOpen
    },

    // Navigation
    setCurrentPage: (state, action: PayloadAction<string>) => {
      state.currentPage = action.payload
    },

    setBreadcrumbs: (
      state,
      action: PayloadAction<Array<{ label: string; path?: string }>>
    ) => {
      state.breadcrumbs = action.payload
    },

    // Toast notifications
    addToast: (
      state,
      action: PayloadAction<Omit<Toast, 'id' | 'isVisible' | 'timestamp'>>
    ) => {
      const toast: Toast = {
        ...action.payload,
        id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        isVisible: true,
        timestamp: Date.now(),
        duration: action.payload.duration || 5000,
      }
      state.toasts.push(toast)
    },

    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload)
    },

    hideToast: (state, action: PayloadAction<string>) => {
      const toast = state.toasts.find(t => t.id === action.payload)
      if (toast) {
        toast.isVisible = false
      }
    },

    clearAllToasts: state => {
      state.toasts = []
    },

    // Modal management
    openModal: (state, action: PayloadAction<Omit<Modal, 'isOpen'>>) => {
      const existingModalIndex = state.modals.findIndex(
        modal => modal.id === action.payload.id
      )
      const modal: Modal = {
        ...action.payload,
        isOpen: true,
      }

      if (existingModalIndex >= 0) {
        state.modals[existingModalIndex] = modal
      } else {
        state.modals.push(modal)
      }
    },

    closeModal: (state, action: PayloadAction<string>) => {
      const modal = state.modals.find(m => m.id === action.payload)
      if (modal) {
        modal.isOpen = false
      }
    },

    removeModal: (state, action: PayloadAction<string>) => {
      state.modals = state.modals.filter(modal => modal.id !== action.payload)
    },

    closeAllModals: state => {
      state.modals.forEach(modal => {
        modal.isOpen = false
      })
    },

    // Loading state management
    setLoading: (
      state,
      action: PayloadAction<{ key: string; loading: boolean }>
    ) => {
      state.loading[action.payload.key] = action.payload.loading
    },

    clearLoading: (state, action: PayloadAction<string>) => {
      delete state.loading[action.payload]
    },

    // Error state management
    setError: (
      state,
      action: PayloadAction<{ key: string; error: string }>
    ) => {
      state.errors[action.payload.key] = action.payload.error
    },

    clearError: (state, action: PayloadAction<string>) => {
      delete state.errors[action.payload]
    },

    clearAllErrors: state => {
      state.errors = {}
    },

    // Feature flag management
    setFeature: (
      state,
      action: PayloadAction<{ feature: string; enabled: boolean }>
    ) => {
      state.features[action.payload.feature] = action.payload.enabled
    },

    // Preference management
    updatePreferences: (
      state,
      action: PayloadAction<Partial<UIState['preferences']>>
    ) => {
      state.preferences = { ...state.preferences, ...action.payload }
    },

    resetPreferences: state => {
      state.preferences = initialState.preferences
    },
  },
})

// Export actions
export const {
  setTheme,
  setSidebarOpen,
  setSidebarCollapsed,
  toggleSidebar,
  setCurrentPage,
  setBreadcrumbs,
  addToast,
  removeToast,
  hideToast,
  clearAllToasts,
  openModal,
  closeModal,
  removeModal,
  closeAllModals,
  setLoading,
  clearLoading,
  setError,
  clearError,
  clearAllErrors,
  setFeature,
  updatePreferences,
  resetPreferences,
} = uiSlice.actions

// Export selectors
export const selectUI = (state: { ui: UIState }) => state.ui
export const selectTheme = (state: { ui: UIState }) => state.ui.theme
export const selectSidebarState = (state: { ui: UIState }) => ({
  open: state.ui.sidebarOpen,
  collapsed: state.ui.sidebarCollapsed,
})
export const selectCurrentPage = (state: { ui: UIState }) =>
  state.ui.currentPage
export const selectBreadcrumbs = (state: { ui: UIState }) =>
  state.ui.breadcrumbs
export const selectToasts = (state: { ui: UIState }) =>
  state.ui.toasts.filter(toast => toast.isVisible)
export const selectModals = (state: { ui: UIState }) =>
  state.ui.modals.filter(modal => modal.isOpen)
export const selectLoading = (key: string) => (state: { ui: UIState }) =>
  state.ui.loading[key] || false
export const selectError = (key: string) => (state: { ui: UIState }) =>
  state.ui.errors[key]
export const selectFeature = (feature: string) => (state: { ui: UIState }) =>
  state.ui.features[feature] || false
export const selectPreferences = (state: { ui: UIState }) =>
  state.ui.preferences

// Export reducer
export default uiSlice.reducer
